import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  BellRing,
  Check,
  CheckCircle2,
  Clipboard,
  Clock3,
  CreditCard,
  Gift,
  MapPin,
  MessageCircle,
  PackageCheck,
  ShieldCheck,
  Store,
  Truck,
  Wifi,
  WifiOff,
} from "lucide-react";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  Button,
  ErrorState,
  MediaImage,
  Modal,
  Notice,
  Skeleton,
  StatusBadge,
} from "../components/ui";
import { TurnstileWidget } from "../components/TurnstileWidget";
import { CheckoutAddressSearch } from "../components/CheckoutAddressSearch";
import { useCart } from "../context/CartContext";
import { useStore } from "../context/StoreContext";
import { useMember } from "../context/MemberContext";
import { ApiError, apiRequest, jsonBody } from "../lib/api";
import {
  ADDRESS_SELECTION_REQUIRED_MESSAGE,
  LOCATION_CONFIRMATION_REQUIRED_MESSAGE,
  activeConfirmedDestination,
  candidateDeliveryLocation,
  confirmDeliveryPin,
  resolveCheckoutDestination,
} from "../lib/checkout-address";
import {
  deriveCheckoutShippingSummary,
  SHIPPING_V2_PENDING_LABEL,
  shippingV2AreaDistanceLabel,
} from "../lib/checkout-shipping-summary";
import {
  formatDateTimeWithZone,
  formatRupiah,
  statusLabel,
} from "../lib/format";
import { normalizeOrder, type Order } from "../lib/types";
import { formatOrderStageDuration } from "../../shared/order-alert";
import {
  MEMBER_POINT_VALUE_RUPIAH,
  maximumRedeemableMemberPoints,
  memberPointDiscount,
} from "../../shared/loyalty";

interface CheckoutForm {
  name: string;
  whatsappNumber: string;
  deliveryType: "DELIVERY" | "PICKUP";
  address: string;
  landmark: string;
  paymentMethodId: string;
  note: string;
}

interface CheckoutPreview {
  branchId: string;
  items: Array<{
    productId: string;
    name: string;
    unit: string;
    unitPrice: number;
    quantity: number;
    availableQuantity: number;
    subtotal: number;
  }>;
  subtotal: number;
  deliveryFee: number;
  discount: number;
  pointsBalance: number;
  pointsRedeemed: number;
  maximumRedeemablePoints: number;
  total: number;
  serviceDate: string;
  deferredToNextDay: boolean;
  serviceTimeZoneLabel: string;
  shippingPath: "LEGACY" | "V2";
  shippingV2QuoteId: string | null;
}

interface ShippingV2QuoteProof {
  checkoutId: string;
  sessionToken: string;
  destination: { lat: number; lng: number };
  quoteId: string;
  display: {
    zone: "INSIDE_DENPASAR" | "OUTSIDE_DENPASAR";
    rawRoadKm: number | null;
    reused?: boolean;
  };
}

interface ShippingV2QuoteResponse {
  status: "FRESH" | "REUSED";
  quote: {
    id: string;
    fee: number;
    total: number;
    subtotal: number;
    zone: "INSIDE_DENPASAR" | "OUTSIDE_DENPASAR";
    rawRoadKm: number | null;
  };
}

const LAST_ORDER_KEY = "sedapsayur_last_order_access";

interface PublicSecurityConfig {
  enabled: boolean;
  siteKey: string;
  actions: {
    orderLookup: string;
    orderCreate: string;
  };
}

function usePublicSecurityConfig() {
  const [config, setConfig] = useState<PublicSecurityConfig>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  useEffect(() => {
    let cancelled = false;
    void apiRequest<PublicSecurityConfig>("/api/public/security-config")
      .then((value) => {
        if (!cancelled) setConfig(value);
      })
      .catch((caught) => {
        if (!cancelled) {
          setError(caught instanceof Error
            ? caught.message
            : "Pemeriksaan keamanan belum dapat dimuat.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return { config, loading, error };
}

export function CheckoutPage() {
  const cart = useCart();
  const { store, activeBranchId } = useStore();
  const { member, refresh: refreshMember } = useMember();
  const navigate = useNavigate();
  const security = usePublicSecurityConfig();
  const [form, setForm] = useState<CheckoutForm>({
    name: member?.name ?? "",
    whatsappNumber: "",
    deliveryType: store?.deliverySettings.deliveryEnabled
      ? "DELIVERY"
      : "PICKUP",
    address: "",
    landmark: "",
    paymentMethodId: String(store?.paymentMethods[0]?.id ?? ""),
    note: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [addressSearching, setAddressSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState<CheckoutPreview>();
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState<string>();
  const [redeemPoints, setRedeemPoints] = useState("0");
  const [idempotencyKey, setIdempotencyKey] = useState(() =>
    crypto.randomUUID(),
  );
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [shippingV2Quote, setShippingV2Quote] = useState<ShippingV2QuoteProof>();
  const [deliveryLocation, setDeliveryLocation] = useState<
    ReturnType<typeof candidateDeliveryLocation> | undefined
  >();
  const [shippingCheckoutId] = useState(() => crypto.randomUUID());
  const [shippingSessionToken] = useState(() => crypto.randomUUID());

  if (cart.items.length === 0 && !submitted)
    return <Navigate to="/keranjang" replace />;

  const effectivePaymentMethodId =
    form.paymentMethodId || String(store?.paymentMethods[0]?.id ?? "");

  const delivery = store?.deliverySettings;
  const shippingV2AddressSearchRequired =
    store?.activeBranch?.shippingV2Enabled === true &&
    form.deliveryType === "DELIVERY";
  const activeBranchKey = activeBranchId === undefined
    ? ""
    : String(activeBranchId);
  const activeDeliveryLocation =
    deliveryLocation?.branchId === activeBranchKey
      ? deliveryLocation
      : undefined;
  const activeConfirmedDeliveryPoint = activeConfirmedDestination(
    activeDeliveryLocation,
    activeBranchKey,
    form.address,
  );
  const deliveryFee =
    form.deliveryType === "PICKUP" || delivery?.feeType === "FREE"
      ? 0
      : delivery?.feeType === "MINIMUM_ORDER_FREE_DELIVERY" &&
          delivery.freeDeliveryMinimum &&
          cart.subtotal >= delivery.freeDeliveryMinimum
        ? 0
        : (delivery?.flatFee ?? 0);
  const clientMaximumPoints = member
    ? maximumRedeemableMemberPoints(member.pointsBalance, cart.subtotal)
    : 0;
  const selectedPoints = Math.min(
    clientMaximumPoints,
    Math.max(0, Number.parseInt(redeemPoints || "0", 10) || 0),
  );
  const pointsDiscount = memberPointDiscount(selectedPoints);
  const total = cart.subtotal + deliveryFee - pointsDiscount;
  const shippingSummary = deriveCheckoutShippingSummary({
    shippingV2Enabled: store?.activeBranch?.shippingV2Enabled === true,
    deliveryType: form.deliveryType,
    legacyDeliveryFee: deliveryFee,
    legacyTotal: total,
    hasShippingV2Quote: shippingV2Quote !== undefined,
    preview,
  });
  const checkoutActionMessage = shippingV2AddressSearchRequired
    ? message ?? Object.values(errors).find(Boolean)
    : undefined;

  const update = (key: keyof CheckoutForm, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: "" }));
    setPreview(undefined);
    setShippingV2Quote(undefined);
    setTurnstileToken("");
    setIdempotencyKey(crypto.randomUUID());
  };

  const orderPayload = (quote = shippingV2Quote) => ({
    branchId: activeBranchId,
    customerName: (form.name || member?.name || "").trim(),
    whatsappNumber: (member?.whatsappNumber ?? form.whatsappNumber).trim(),
    deliveryType: form.deliveryType,
    deliveryAddress:
      form.deliveryType === "DELIVERY" ? form.address.trim() : undefined,
    deliveryLandmark: form.landmark.trim() || undefined,
    paymentMethodId: effectivePaymentMethodId,
    customerNote: form.note.trim() || undefined,
    items: cart.items.map((item) => ({
      productId: item.product.id,
      quantity: item.quantity,
    })),
    redeemPoints: selectedPoints,
    ...(quote
      ? {
          shippingV2Quote: {
            checkoutId: quote.checkoutId,
            sessionToken: quote.sessionToken,
            destination: quote.destination,
            quoteId: quote.quoteId,
          },
        }
      : {}),
  });

  const validateForm = () => {
    const next: Record<string, string> = {};
    if ((form.name || member?.name || "").trim().length < 2)
      next.name = "Masukkan nama lengkap Anda.";
    const phoneDigits = (member?.whatsappNumber ?? form.whatsappNumber)
      .replace(/\D/g, "")
      .replace(/^00/u, "");
    if (phoneDigits.length < 8 || phoneDigits.length > 15)
      next.whatsappNumber =
        "Masukkan nomor WhatsApp beserta kode negara yang benar.";
    if (form.deliveryType === "DELIVERY" && form.address.trim().length < 3)
      next.address = shippingV2AddressSearchRequired
        ? "Masukkan patokan untuk ongkir minimal 3 karakter."
        : "Tuliskan alamat lengkap agar mudah ditemukan.";
    if (shippingV2AddressSearchRequired && !activeDeliveryLocation?.suggestion.point)
      next.address = ADDRESS_SELECTION_REQUIRED_MESSAGE;
    else if (shippingV2AddressSearchRequired && !activeConfirmedDeliveryPoint)
      next.address = LOCATION_CONFIRMATION_REQUIRED_MESSAGE;
    if (shippingV2AddressSearchRequired && form.landmark.trim().length < 3)
      next.landmark =
        "Masukkan gang, blok, nomor rumah, atau ciri rumah agar alamat lengkap.";
    if (!effectivePaymentMethodId)
      next.paymentMethodId = "Pilih metode pembayaran.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || (shippingV2AddressSearchRequired && addressSearching)) return;
    if (!validateForm() || activeBranchId === undefined) return;
    setBusy(true);
    setMessage(undefined);
    try {
      let quoteProof: ShippingV2QuoteProof | undefined;
      if (
        store?.activeBranch?.shippingV2Enabled === true &&
        form.deliveryType === "DELIVERY"
      ) {
        try {
          const destination = await resolveCheckoutDestination({
            addressSearchRequired: shippingV2AddressSearchRequired,
            confirmedPoint: activeConfirmedDeliveryPoint,
          });
          const quote = await apiRequest<ShippingV2QuoteResponse>(
            "/api/shipping/quote",
            {
              method: "POST",
              ...jsonBody({
                checkoutId: shippingCheckoutId,
                sessionToken: shippingSessionToken,
                actionId: crypto.randomUUID(),
                branchId: activeBranchId,
                deliveryType: form.deliveryType,
                destination,
                locationConfirmed: true,
                items: cart.items.map((item) => ({
                  productId: item.product.id,
                  quantity: item.quantity,
                })),
              }),
            },
          );
          quoteProof = {
            checkoutId: shippingCheckoutId,
            sessionToken: shippingSessionToken,
            destination,
            quoteId: quote.quote.id,
            display: {
              zone: quote.quote.zone,
              rawRoadKm: quote.quote.rawRoadKm,
              reused: quote.status === "REUSED",
            },
          };
        } catch (caught) {
          if (
            !(caught instanceof ApiError) ||
            !["SHIPPING_V2_NOT_ENABLED", "SHIPPING_V2_UNAVAILABLE"].includes(caught.code ?? "")
          ) {
            throw caught;
          }
          // A stale public branch hint may briefly say V2 after the
          // authoritative runtime/branch gate was switched OFF. Fall back to
          // the unchanged legacy validation path without retaining a quote.
          quoteProof = undefined;
        }
      }
      const validation = await apiRequest<CheckoutPreview>(
        "/api/public/orders/validate",
        {
          method: "POST",
          ...jsonBody(orderPayload(quoteProof)),
        },
      );
      const acceptedProof = validation.shippingPath === "V2"
        ? quoteProof
        : undefined;
      setShippingV2Quote(acceptedProof);
      setPreview(validation);
    } catch (caught) {
      setMessage(
        caught instanceof Error
          ? caught.message
          : "Pesanan belum dapat diperiksa.",
      );
    } finally {
      setBusy(false);
    }
  };

  const confirmOrder = async () => {
    if (!preview || activeBranchId === undefined) return;
    if (!turnstileToken) {
      setMessage("Tunggu pemeriksaan keamanan selesai sebelum membuat pesanan.");
      return;
    }
    setCreating(true);
    setMessage(undefined);
    try {
      const payload = await apiRequest<unknown>("/api/public/orders", {
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        ...jsonBody({ ...orderPayload(), turnstileToken }),
      });
      const order = normalizeOrder(payload);
      localStorage.setItem(
        LAST_ORDER_KEY,
        JSON.stringify({
          orderNumber: order.orderNumber,
          whatsappNumber: (member?.whatsappNumber ?? form.whatsappNumber).trim(),
          liveToken: order.liveToken,
        }),
      );
      setSubmitted(true);
      void refreshMember();
      void navigate(`/pesanan/${encodeURIComponent(order.orderNumber)}`, {
        replace: true,
        state: { order },
      });
      cart.clearCart();
    } catch (caught) {
      setMessage(
        caught instanceof Error
          ? caught.message
          : "Pesanan belum berhasil dibuat.",
      );
      setTurnstileToken("");
      setTurnstileResetKey((value) => value + 1);
    } finally {
      setCreating(false);
    }
  };

  const selectedPayment = store?.paymentMethods.find(
    (method) => String(method.id) === effectivePaymentMethodId,
  );

  return (
    <div className="page-shell inner-page checkout-page">
      <div className="page-heading">
        <span className="eyebrow">Satu langkah lagi</span>
        <h1>Buat Pesanan</h1>
        <p>Isi data singkat berikut. Member dapat memakai poin sebagai potongan.</p>
      </div>
      {message && <Notice tone="danger">{message}</Notice>}
      <form
        className="checkout-layout"
        onSubmit={(event) => void submit(event)}
        noValidate
      >
        <div className="checkout-form">
          <fieldset className="form-card">
            <legend>
              <span>1</span> Data Pembeli
            </legend>
            <label>
              Nama lengkap
              <input
                autoComplete="name"
                value={form.name || member?.name || ""}
                onChange={(event) => update("name", event.target.value)}
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? "name-error" : undefined}
              />
            </label>
            {errors.name && (
              <small className="field-error" id="name-error">
                {errors.name}
              </small>
            )}
            <label>
              Nomor WhatsApp
              <input
                inputMode="tel"
                autoComplete="tel"
                placeholder="Masukkan kode negara + nomor WhatsApp"
                value={member?.whatsappNumber ?? form.whatsappNumber}
                onChange={(event) =>
                  update("whatsappNumber", event.target.value)
                }
                readOnly={Boolean(member)}
                aria-invalid={Boolean(errors.whatsappNumber)}
                aria-describedby={
                  errors.whatsappNumber ? "phone-error" : "phone-help"
                }
              />
            </label>
            <small id="phone-help">
              Nomor Indonesia boleh ditulis +628... atau 08...; keduanya terbaca
              sama. Nomor negara lain tetap dapat memakai kode negara.
            </small>
            {errors.whatsappNumber && (
              <small className="field-error" id="phone-error">
                {errors.whatsappNumber}
              </small>
            )}
            {member ? (
              <div className="member-checkout-card">
                <Gift aria-hidden="true" />
                <div>
                  <strong>{member.pointsBalance} poin tersedia</strong>
                  <small>
                    1 poin = potongan {formatRupiah(MEMBER_POINT_VALUE_RUPIAH)}
                  </small>
                </div>
                <label>
                  Poin digunakan
                  <input
                    inputMode="numeric"
                    type="number"
                    min="0"
                    max={clientMaximumPoints}
                    value={redeemPoints}
                    onChange={(event) => {
                      setRedeemPoints(event.target.value);
                      setPreview(undefined);
                      setIdempotencyKey(crypto.randomUUID());
                    }}
                  />
                </label>
              </div>
            ) : (
              <Notice tone="info">
                <Link to="/member">Masuk sebagai member</Link> untuk memakai dan mengumpulkan poin.
              </Notice>
            )}
          </fieldset>

          <fieldset className="form-card">
            <legend>
              <span>2</span> Cara Menerima
            </legend>
            <div className="choice-grid">
              {delivery?.deliveryEnabled && (
                <label
                  className={`choice-card ${form.deliveryType === "DELIVERY" ? "choice-card--selected" : ""}`}
                >
                  <input
                    type="radio"
                    name="deliveryType"
                    checked={form.deliveryType === "DELIVERY"}
                    onChange={() => update("deliveryType", "DELIVERY")}
                  />
                  <Truck aria-hidden="true" />
                  <span>
                    <strong>Antar ke Alamat</strong>
                    <small>Diantar dari cabang aktif</small>
                  </span>
                </label>
              )}
              {delivery?.pickupEnabled && (
                <label
                  className={`choice-card ${form.deliveryType === "PICKUP" ? "choice-card--selected" : ""}`}
                >
                  <input
                    type="radio"
                    name="deliveryType"
                    checked={form.deliveryType === "PICKUP"}
                    onChange={() => update("deliveryType", "PICKUP")}
                  />
                  <Store aria-hidden="true" />
                  <span>
                    <strong>Ambil di Toko</strong>
                    <small>Ambil selama jam operasional</small>
                  </span>
                </label>
              )}
            </div>
            {!delivery?.deliveryEnabled && (
              <Notice tone="info">
                Pengiriman dari {store?.activeBranch?.name} belum aktif. Pilih
                Ambil di Toko atau ganti cabang ke Sedap Malam untuk layanan
                antar.
              </Notice>
            )}
            {store?.activeBranch?.shippingV2Enabled === true &&
              form.deliveryType === "DELIVERY" && (
                <Notice tone="info">
                  Cari patokan dekat rumah, lalu pilih hasil alamat yang paling
                  sesuai. Ongkir akan dihitung otomatis dari lokasi tersebut.
                </Notice>
              )}
            {delivery?.deliveryEnabled &&
              delivery.deliveryWindows.length > 0 && (
                <div className="checkout-delivery-info">
                  <strong>Waktu pengiriman (WITA)</strong>
                  {delivery.deliveryWindows.map((window) => (
                    <span key={`${window.label}-${window.startTime}`}>
                      {window.startTime}–{window.endTime} · last order pukul {" "}
                      {window.cutoffTime}
                    </span>
                  ))}
                  {delivery.freeDeliveryMinimum && (
                    <small>
                      Gratis ongkir {delivery.deliveryArea ?? "Area Denpasar"}
                      {" "}minimal belanja {formatRupiah(
                        delivery.freeDeliveryMinimum,
                      )}
                      {store?.activeBranch?.shippingV2Enabled === true &&
                      store.activeBranch.shippingV2FreeRadiusEnabled === true &&
                      store.activeBranch.shippingV2FreeRadiusKm !== undefined
                        ? ` (maksimum ${store.activeBranch.shippingV2FreeRadiusKm.toLocaleString("id-ID")} km; lebih dari ${store.activeBranch.shippingV2FreeRadiusKm.toLocaleString("id-ID")} km mengikuti tarif toko).`
                        : "."}
                    </small>
                  )}
                  {shippingV2AddressSearchRequired && (
                    <small className="checkout-delivery-info__address-guidance">
                      Isi patokan dekat dengan alamat rumah agar mendapatkan
                      ongkir yang akurat.
                    </small>
                  )}
                </div>
              )}
            {form.deliveryType === "DELIVERY" ? (
              <div className="conditional-fields">
                {shippingV2AddressSearchRequired ? (
                  <CheckoutAddressSearch
                    key={`${activeBranchKey}:${shippingCheckoutId}`}
                    checkoutId={shippingCheckoutId}
                    branchId={activeBranchKey}
                    value={form.address}
                    selected={activeDeliveryLocation?.suggestion}
                    error={errors.address}
                    onLoadingChange={setAddressSearching}
                    onTextChange={(value) => {
                      if (activeDeliveryLocation && value.trim() === form.address.trim()) {
                        setForm((current) => ({ ...current, address: value }));
                        return;
                      }
                      setDeliveryLocation(undefined);
                      update("address", value);
                    }}
                    onSelect={(suggestion) => {
                      if (activeBranchId === undefined) return;
                      const candidate = candidateDeliveryLocation(
                        String(activeBranchId),
                        suggestion,
                      );
                      setDeliveryLocation(confirmDeliveryPin(
                        candidate,
                        form.address,
                      ));
                      setErrors((current) => ({ ...current, address: "" }));
                      setPreview(undefined);
                      setShippingV2Quote(undefined);
                      setTurnstileToken("");
                      setIdempotencyKey(crypto.randomUUID());
                      setMessage(undefined);
                    }}
                  />
                ) : (
                  <>
                    <label>
                      Alamat lengkap
                      <textarea
                        autoComplete="street-address"
                        rows={4}
                        value={form.address}
                        onChange={(event) => update("address", event.target.value)}
                        aria-invalid={Boolean(errors.address)}
                        aria-describedby={
                          errors.address ? "address-error" : undefined
                        }
                      />
                    </label>
                    {errors.address && (
                      <small className="field-error" id="address-error">
                        {errors.address}
                      </small>
                    )}
                  </>
                )}
                <label className="checkout-landmark-label">
                  {shippingV2AddressSearchRequired
                    ? "Detail alamat lengkap dengan nomor rumah"
                    : <>Patokan tambahan <span>(opsional)</span></>}
                  <input
                    value={form.landmark}
                    onChange={(event) => update("landmark", event.target.value)}
                    placeholder={shippingV2AddressSearchRequired
                      ? "Contoh: Blok C No. 26, Gang Permai No. 2, pagar merah, atau cat kuning"
                      : "Contoh: rumah pagar hijau"}
                    aria-invalid={Boolean(errors.landmark)}
                    aria-describedby={errors.landmark ? "landmark-error" : undefined}
                  />
                </label>
                {errors.landmark && (
                  <small className="field-error" id="landmark-error">
                    {errors.landmark}
                  </small>
                )}
              </div>
            ) : (
              <div className="pickup-card">
                <MapPin aria-hidden="true" />
                <div>
                  <strong>{store?.activeBranch?.name}</strong>
                  <span>{store?.activeBranch?.address}</span>
                  <small>
                    Buka {store?.activeBranch?.openingTime}–
                    {store?.activeBranch?.closingTime}
                  </small>
                </div>
              </div>
            )}
          </fieldset>

          <fieldset className="form-card">
            <legend>
              <span>3</span> Pembayaran
            </legend>
            <div className="payment-list">
              {store?.paymentMethods.map((method) => (
                <label
                  className={`payment-option ${effectivePaymentMethodId === String(method.id) ? "payment-option--selected" : ""}`}
                  key={String(method.id)}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value={String(method.id)}
                    checked={effectivePaymentMethodId === String(method.id)}
                    onChange={(event) =>
                      update("paymentMethodId", event.target.value)
                    }
                  />
                  {method.mediaId ? (
                    <span className="payment-option__logo">
                      <MediaImage
                        mediaId={method.mediaId}
                        alt={`Logo ${method.name}`}
                      />
                    </span>
                  ) : (
                    <CreditCard aria-hidden="true" />
                  )}
                  <span>
                    <strong>{method.name}</strong>
                    <small>
                      {method.instructions ??
                        "Pembayaran dikonfirmasi oleh cabang."}
                    </small>
                  </span>
                </label>
              ))}
            </div>
            {errors.paymentMethodId && (
              <small className="field-error">{errors.paymentMethodId}</small>
            )}
            {selectedPayment?.accountNumber && (
              <div className="bank-details">
                <span>Nomor rekening</span>
                <strong>{selectedPayment.accountNumber}</strong>
                <small>a.n. {selectedPayment.accountName}</small>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    void navigator.clipboard.writeText(
                      selectedPayment.accountNumber ?? "",
                    )
                  }
                  icon={<Clipboard aria-hidden="true" size={18} />}
                >
                  Salin Nomor
                </Button>
              </div>
            )}
            {selectedPayment?.type.toUpperCase().includes("QRIS") &&
              selectedPayment.mediaId !== undefined && (
                <div className="qris-card">
                  <MediaImage
                    mediaId={selectedPayment.mediaId}
                    alt="Kode QRIS Warung Sedap Sayur"
                  />
                  <p>Scan QRIS, lalu konfirmasi melalui WhatsApp.</p>
                </div>
              )}
            <label>
              Catatan pesanan <span>(opsional)</span>
              <textarea
                rows={3}
                value={form.note}
                onChange={(event) => update("note", event.target.value)}
                placeholder="Contoh: pilih sayur yang masih muda"
              />
            </label>
          </fieldset>
        </div>

        <aside className="order-summary order-summary--checkout">
          <h2>Ringkasan Pesanan</h2>
          <p className="summary-branch">
            <Store aria-hidden="true" /> {store?.activeBranch?.name}
          </p>
          <div className="summary-items">
            {cart.items.map((item) => (
              <div key={String(item.product.id)}>
                <span>
                  {item.quantity}× {item.product.name}
                </span>
                <strong>
                  {formatRupiah(
                    (item.product.promoPrice ?? item.product.price) *
                      item.quantity,
                  )}
                </strong>
              </div>
            ))}
          </div>
          <div>
            <span>Subtotal</span>
            <strong>{formatRupiah(cart.subtotal)}</strong>
          </div>
          {pointsDiscount > 0 && (
            <div>
              <span>Potongan {selectedPoints} poin</span>
              <strong>-{formatRupiah(pointsDiscount)}</strong>
            </div>
          )}
          <div>
            <span>
              {form.deliveryType === "PICKUP" ? "Ambil di toko" : "Ongkir"}
            </span>
            <span className="checkout-shipping-value">
              <strong>
              {shippingSummary.pending
                ? SHIPPING_V2_PENDING_LABEL
                : shippingSummary.deliveryFee === 0
                  ? "Gratis"
                  : formatRupiah(shippingSummary.deliveryFee)}
              </strong>
              {!shippingSummary.pending && shippingV2Quote?.display && (
                <small>{shippingV2AreaDistanceLabel(shippingV2Quote.display)}</small>
              )}
            </span>
          </div>
          <div className="summary-total">
            <span>Total</span>
            <strong>{formatRupiah(shippingSummary.total)}</strong>
          </div>
          {checkoutActionMessage && (
            <Notice tone="danger">{checkoutActionMessage}</Notice>
          )}
          {shippingV2AddressSearchRequired && addressSearching && (
            <Notice tone="info">Mencari alamat… Tunggu hasilnya lalu pilih patokan untuk ongkir.</Notice>
          )}
          {shippingV2Quote?.display.reused && (
            <Notice tone="info">Alamat sama. Jarak sebelumnya digunakan kembali tanpa pencarian rute baru; ongkir tetap mengikuti belanja dan aturan toko.</Notice>
          )}
          <Button
            type="submit"
            busy={busy}
            disabled={shippingV2AddressSearchRequired && addressSearching}
            className="button--full"
            icon={<PackageCheck aria-hidden="true" />}
          >
            Periksa Pesanan
          </Button>
          <small>
            Harga dan stok dihitung ulang oleh sistem sebelum pesanan disimpan.
          </small>
        </aside>
      </form>
      <Modal
        open={Boolean(preview)}
        title="Periksa Pesanan Sekali Lagi"
        description="Pastikan cabang, nomor WhatsApp, dan isi pesanan sudah benar sebelum disimpan."
        onClose={() => {
          setPreview(undefined);
          setTurnstileToken("");
        }}
      >
        {preview && (
          <div className="checkout-review">
            <dl className="review-facts">
              <div>
                <dt>Cabang</dt>
                <dd>{store?.activeBranch?.name}</dd>
              </div>
              <div>
                <dt>Nama</dt>
                <dd>{(form.name || member?.name || "").trim()}</dd>
              </div>
              <div>
                <dt>WhatsApp</dt>
                <dd>{(member?.whatsappNumber ?? form.whatsappNumber).trim()}</dd>
              </div>
              <div>
                <dt>Cara menerima</dt>
                <dd>
                  {form.deliveryType === "DELIVERY"
                    ? "Antar ke alamat"
                    : "Ambil di toko"}
                </dd>
              </div>
              {form.deliveryType === "DELIVERY" && (
                <>
                  <div>
                    <dt>{shippingV2AddressSearchRequired ? "Patokan ongkir" : "Alamat"}</dt>
                    <dd>{form.address.trim()}</dd>
                  </div>
                  {form.landmark.trim() && (
                    <div>
                      <dt>{shippingV2AddressSearchRequired ? "Detail alamat" : "Patokan"}</dt>
                      <dd>{form.landmark.trim()}</dd>
                    </div>
                  )}
                </>
              )}
            </dl>
            <div className="review-lines">
              {preview.items.map((item) => (
                <div key={item.productId}>
                  <span>
                    <strong>
                      {item.quantity}× {item.name}
                    </strong>
                    <small>
                      {formatRupiah(item.unitPrice)} / {item.unit}
                    </small>
                  </span>
                  <strong>{formatRupiah(item.subtotal)}</strong>
                </div>
              ))}
            </div>
            <div className="order-totals">
              <div>
                <span>Subtotal</span>
                <strong>{formatRupiah(preview.subtotal)}</strong>
              </div>
              <div>
                <span>Ongkir</span>
                <span className="checkout-shipping-value">
                  <strong>
                    {preview.deliveryFee
                      ? formatRupiah(preview.deliveryFee)
                      : "Gratis"}
                  </strong>
                  {shippingV2Quote?.display && preview.shippingPath === "V2" && (
                    <small>{shippingV2AreaDistanceLabel(shippingV2Quote.display)}</small>
                  )}
                </span>
              </div>
              {preview.discount > 0 && (
                <div>
                  <span>Potongan {preview.pointsRedeemed} poin</span>
                  <strong>-{formatRupiah(preview.discount)}</strong>
                </div>
              )}
              <div>
                <span>Total</span>
                <strong>{formatRupiah(preview.total)}</strong>
              </div>
            </div>
            <Notice tone="info">
              Setelah pesanan dibuat, Anda akan mendapat nomor pesanan. Simpan
              nomor tersebut, lalu tunggu Admin cabang mengonfirmasi pesanan
              Anda.
            </Notice>
            {preview.deferredToNextDay && (
              <Notice tone="warning">
                Last order hari ini sudah lewat. Pesanan ini tetap diterima dan
                akan diproses besok, {preview.serviceDate} (
                {preview.serviceTimeZoneLabel}).
              </Notice>
            )}
            {message && <Notice tone="danger">{message}</Notice>}
            <div className="member-security-check">
              <span><ShieldCheck aria-hidden="true" /> Anti-spam aman dari Cloudflare</span>
              {security.loading && <small>Memuat pemeriksaan keamanan…</small>}
              {security.error && <Notice tone="danger">{security.error}</Notice>}
              {security.config?.enabled && security.config.siteKey && (
                <TurnstileWidget
                  siteKey={security.config.siteKey}
                  action={security.config.actions.orderCreate}
                  resetKey={turnstileResetKey}
                  label="Pemeriksaan keamanan pembuatan pesanan"
                  onToken={(token) => {
                    setTurnstileToken(token);
                    if (token) setMessage(undefined);
                  }}
                  onError={setMessage}
                />
              )}
              {security.config && !security.config.enabled && (
                <Notice tone="warning">Pembuatan pesanan online sedang dipersiapkan. Hubungi cabang melalui WhatsApp.</Notice>
              )}
            </div>
            <div className="modal-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setPreview(undefined);
                  setTurnstileToken("");
                }}
                disabled={creating}
              >
                Ubah Data
              </Button>
              <Button
                type="button"
                busy={creating}
                disabled={!security.config?.enabled || !turnstileToken}
                onClick={() => void confirmOrder()}
                icon={<PackageCheck aria-hidden="true" />}
              >
                Konfirmasi & Buat Pesanan
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function OrderLookupForm({
  initialOrderNumber = "",
  onFound,
}: {
  initialOrderNumber?: string;
  onFound?: (order: Order) => void;
}) {
  const { store } = useStore();
  const mode = store?.orderLookupMode ?? "ORDER_AND_PHONE";
  const navigate = useNavigate();
  const security = usePublicSecurityConfig();
  const [orderNumber, setOrderNumber] = useState(initialOrderNumber);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!turnstileToken) {
      setError("Tunggu pemeriksaan keamanan selesai sebelum mengecek pesanan.");
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const payload = await apiRequest<unknown>("/api/public/orders/lookup", {
        method: "POST",
        ...jsonBody({
          orderNumber: mode === "PHONE_ONLY" ? undefined : orderNumber.trim(),
          whatsappNumber: mode === "ORDER_ONLY" ? undefined : phone.trim(),
          turnstileToken,
        }),
      });
      const order = normalizeOrder(payload);
      localStorage.setItem(
        LAST_ORDER_KEY,
        JSON.stringify({
          orderNumber: order.orderNumber,
          whatsappNumber: phone.trim(),
          liveToken: order.liveToken,
        }),
      );
      if (onFound) onFound(order);
      else
        void navigate(`/pesanan/${encodeURIComponent(order.orderNumber)}`, {
          state: { order },
        });
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Pesanan tidak ditemukan.",
      );
      setTurnstileToken("");
      setTurnstileResetKey((value) => value + 1);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="lookup-card" onSubmit={(event) => void submit(event)}>
      <div className="lookup-card__icon">
        <PackageCheck aria-hidden="true" />
      </div>
      <h1>Cek Pesanan</h1>
      <p>
        {mode === "ORDER_AND_PHONE"
          ? "Masukkan nomor pesanan dan WhatsApp yang digunakan saat memesan."
          : mode === "ORDER_ONLY"
            ? "Masukkan nomor pesanan Anda."
            : "Masukkan WhatsApp untuk melihat pesanan terbaru pada nomor tersebut."}
      </p>
      {error && <Notice tone="danger">{error}</Notice>}
      {mode !== "PHONE_ONLY" && (
        <>
          <label>
            Nomor pesanan
            <input
              inputMode="numeric"
              pattern="(?:[0-9]{5}|SS-[A-Za-z0-9-]+)"
              value={orderNumber}
              onChange={(event) => setOrderNumber(event.target.value.toUpperCase())}
              placeholder="Contoh: 48217"
              aria-describedby="order-number-help"
              required
            />
          </label>
          <small id="order-number-help">Pesanan baru memakai nomor yang terdiri dari 5 angka.</small>
        </>
      )}
      {mode !== "ORDER_ONLY" && (
        <label>
          Nomor WhatsApp
          <input
            inputMode="tel"
            autoComplete="tel"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="Contoh: +62812... atau 0812..."
            required
          />
        </label>
      )}
      <div className="member-security-check">
        <span><ShieldCheck aria-hidden="true" /> Anti-spam aman dari Cloudflare</span>
        {security.loading && <small>Memuat pemeriksaan keamanan…</small>}
        {security.error && <Notice tone="danger">{security.error}</Notice>}
        {security.config?.enabled && security.config.siteKey && (
          <TurnstileWidget
            siteKey={security.config.siteKey}
            action={security.config.actions.orderLookup}
            resetKey={turnstileResetKey}
            label="Pemeriksaan keamanan cek pesanan"
            onToken={(token) => {
              setTurnstileToken(token);
              if (token) setError(undefined);
            }}
            onError={setError}
          />
        )}
      </div>
      <Button
        type="submit"
        busy={busy}
        disabled={!security.config?.enabled || !turnstileToken}
        className="button--full"
      >
        Cek Status
      </Button>
      {mode !== "ORDER_ONLY" && (
        <small>Nomor +628... dan 08... dikenali sebagai nomor yang sama.</small>
      )}
    </form>
  );
}

export function OrderLookupPage() {
  return (
    <div className="page-shell inner-page lookup-page">
      <OrderLookupForm />
    </div>
  );
}

const deliveryTimelineSteps = [
  "NEW",
  "CONFIRMED",
  "PREPARING",
  "READY_FOR_DELIVERY",
  "DELIVERING",
  "COMPLETED",
];
const pickupTimelineSteps = [
  "NEW",
  "CONFIRMED",
  "PREPARING",
  "READY_FOR_PICKUP",
  "COMPLETED",
];

const timelineStageByOrderStatus: Record<string, string> = {
  WAITING_CONFIRMATION: "NEW",
  WAITING_PAYMENT: "CONFIRMED",
  PAID: "CONFIRMED",
};

const timelineEffectByStep: Record<string, string> = {
  NEW: "new",
  CONFIRMED: "confirmed",
  PREPARING: "preparing",
  READY_FOR_PICKUP: "ready",
  READY_FOR_DELIVERY: "ready",
  DELIVERING: "delivering",
  COMPLETED: "completed",
};

function OrderTimelineCurrentIcon({ status }: { status: string }) {
  if (["READY_FOR_PICKUP", "READY_FOR_DELIVERY"].includes(status)) {
    return <BellRing aria-hidden="true" />;
  }
  if (status === "DELIVERING") return <Truck aria-hidden="true" />;
  if (status === "PREPARING") return <PackageCheck aria-hidden="true" />;
  if (status === "NEW") return <Clipboard aria-hidden="true" />;
  return <CheckCircle2 aria-hidden="true" />;
}

export function OrderStatusPage() {
  const { orderNumber = "" } = useParams();
  const location = useLocation();
  const { store } = useStore();
  const stateOrder = (location.state as { order?: Order } | null)?.order;
  const [order, setOrder] = useState<Order | undefined>(stateOrder);
  const [loading, setLoading] = useState(!stateOrder);
  const [lookupRequired, setLookupRequired] = useState(false);
  const [error, setError] = useState<string>();
  const [liveConnected, setLiveConnected] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const refreshOrder = useCallback(async () => {
    let saved: {
      orderNumber?: string;
      whatsappNumber?: string;
      liveToken?: string;
    } = {};
    try {
      const parsed: unknown = JSON.parse(
        localStorage.getItem(LAST_ORDER_KEY) ?? "{}",
      );
      if (parsed && typeof parsed === "object") {
        saved = {
          orderNumber:
            "orderNumber" in parsed && typeof parsed.orderNumber === "string"
              ? parsed.orderNumber
              : undefined,
          whatsappNumber:
            "whatsappNumber" in parsed &&
            typeof parsed.whatsappNumber === "string"
              ? parsed.whatsappNumber
              : undefined,
          liveToken:
            "liveToken" in parsed && typeof parsed.liveToken === "string"
              ? parsed.liveToken
              : undefined,
        };
      }
    } catch {
      /* empty */
    }
    const mode = store?.orderLookupMode ?? "ORDER_AND_PHONE";
    const hasAccess =
      mode === "ORDER_ONLY"
        ? saved.orderNumber === orderNumber
        : mode === "PHONE_ONLY"
          ? Boolean(saved.whatsappNumber)
          : saved.orderNumber === orderNumber && Boolean(saved.whatsappNumber);
    if (!hasAccess || !saved.liveToken) {
      setLookupRequired(true);
      setLoading(false);
      return;
    }
    const payload = await apiRequest<unknown>("/api/public/orders/lookup", {
      method: "POST",
      ...jsonBody({
        orderNumber: mode === "PHONE_ONLY" ? undefined : orderNumber,
        whatsappNumber: mode === "ORDER_ONLY" ? undefined : saved.whatsappNumber,
        liveToken: saved.liveToken,
      }),
    });
    setOrder(normalizeOrder(payload));
    setLookupRequired(false);
    setError(undefined);
    setLoading(false);
  }, [orderNumber, store?.orderLookupMode]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void refreshOrder().catch((caught) => {
        if (
          caught instanceof ApiError &&
          ["TURNSTILE_REQUIRED", "TURNSTILE_FAILED"].includes(caught.code ?? "")
        ) {
          setLookupRequired(true);
        }
        setError(
          caught instanceof Error ? caught.message : "Pesanan belum dapat dimuat.",
        );
        setLoading(false);
      });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [refreshOrder, stateOrder]);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!order?.liveToken) return;
    let stopped = false;
    let socket: WebSocket | undefined;
    let reconnectTimer: number | undefined;
    const connect = () => {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      socket = new WebSocket(
        `${protocol}//${window.location.host}/api/events/orders/public?ticket=${encodeURIComponent(order.liveToken ?? "")}`,
      );
      socket.addEventListener("open", () => setLiveConnected(true));
      socket.addEventListener("message", (event) => {
        try {
          const message = JSON.parse(String(event.data)) as { type?: string };
          if (message.type === "ORDER_UPDATED") void refreshOrder();
        } catch {
          // Pesan selain JSON diabaikan; data pesanan tetap diambil hanya dari API.
        }
      });
      socket.addEventListener("close", () => {
        setLiveConnected(false);
        if (!stopped) reconnectTimer = window.setTimeout(connect, 2_000);
      });
      socket.addEventListener("error", () => socket?.close());
    };
    connect();
    return () => {
      stopped = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      socket?.close(1000, "Halaman ditutup");
      setLiveConnected(false);
    };
  }, [order?.liveToken, refreshOrder]);

  if (loading)
    return (
      <div className="page-shell inner-page">
        <Skeleton rows={5} />
      </div>
    );
  if (lookupRequired)
    return (
      <div className="page-shell inner-page lookup-page">
        <OrderLookupForm
          initialOrderNumber={orderNumber}
          onFound={(found) => {
            setOrder(found);
            setLookupRequired(false);
            setError(undefined);
          }}
        />
      </div>
    );
  if (error || !order)
    return (
      <div className="page-shell inner-page">
        <ErrorState message={error ?? "Pesanan tidak ditemukan."} />
      </div>
    );

  const timelineSteps =
    order.deliveryType === "PICKUP"
      ? pickupTimelineSteps
      : deliveryTimelineSteps;
  const currentTimelineStage =
    timelineStageByOrderStatus[order.orderStatus] ?? order.orderStatus;
  const currentIndex = timelineSteps.indexOf(currentTimelineStage);
  const cancelled = ["CANCELLED", "REJECTED"].includes(order.orderStatus);
  const confirmationDuration = order.createdAt
    ? formatOrderStageDuration(order.createdAt, order.confirmedAt, now)
    : "-";
  const timelineTimeByStep = new Map<string, string>();
  for (const event of order.statusEvents ?? []) {
    const step = timelineStageByOrderStatus[event.status] ?? event.status;
    if (!timelineTimeByStep.has(step)) {
      timelineTimeByStep.set(step, event.createdAt);
    }
  }
  if (order.createdAt && !timelineTimeByStep.has("NEW")) {
    timelineTimeByStep.set("NEW", order.createdAt);
  }
  if (order.confirmedAt && !timelineTimeByStep.has("CONFIRMED")) {
    timelineTimeByStep.set("CONFIRMED", order.confirmedAt);
  }
  const orderWhatsapp =
    order.branchWhatsapp ??
    store?.branches.find(
      (branch) => String(branch.id) === String(order.branchId),
    )?.whatsappNumber ??
    store?.activeBranch?.whatsappNumber;
  const whatsapp =
    order.whatsappUrl ??
    (orderWhatsapp
      ? `https://wa.me/${orderWhatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(`Halo Warung Sedap Sayur, saya ingin mengonfirmasi pesanan ${order.orderNumber}.`)}`
      : undefined);
  return (
    <div className="page-shell inner-page order-status-page">
      <section className="order-success-banner">
        <CheckCircle2 aria-hidden="true" />
        <div>
          <span>
            {order.orderStatus === "NEW"
              ? "Pesanan Anda berhasil dibuat"
              : "Status pesanan"}
          </span>
          <h1>{order.orderNumber}</h1>
          <p>
            {order.orderStatus === "NEW"
              ? `Mohon tunggu Admin ${order.branchName} mengonfirmasi pesanan Anda.`
              : "Simpan nomor ini untuk mengecek pesanan kembali."}
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => void navigator.clipboard.writeText(order.orderNumber)}
          icon={<Clipboard aria-hidden="true" />}
        >
          Salin Nomor
        </Button>
      </section>
      <div
        className={`order-live-indicator ${liveConnected ? "is-connected" : ""}`}
        role="status"
        aria-live="polite"
      >
        {liveConnected ? <Wifi aria-hidden="true" /> : <WifiOff aria-hidden="true" />}
        <span>
          <strong>{liveConnected ? "Status live aktif" : "Menghubungkan status live"}</strong>
          <small>
            {liveConnected
              ? "Perubahan dari Admin akan tampil otomatis di halaman ini."
              : "Jika koneksi terputus, halaman akan mencoba tersambung kembali."}
          </small>
        </span>
      </div>
      <section className="order-confirmation-time" aria-label="Waktu konfirmasi pesanan">
        <span>{order.confirmedAt ? "Waktu konfirmasi Admin" : "Sedang menunggu Admin"}</span>
        <strong>
          {order.confirmedAt
            ? `Dikonfirmasi dalam ${confirmationDuration}`
            : `Menunggu selama ${confirmationDuration}`}
        </strong>
        <p>
          {order.confirmedAt
            ? `Admin ${order.branchName} sudah menerima dan mengonfirmasi pesanan.`
            : `Admin ${order.branchName} akan memeriksa pesanan. Anda tidak perlu memuat ulang halaman.`}
        </p>
      </section>
      {order.orderStatus === "NEW" && (
        <>
          <Notice tone="success">
            Pesanan sudah masuk ke Admin {order.branchName}. Admin akan
            memeriksa stok dan mengonfirmasi pesanan sebelum diproses.
          </Notice>
          <section
            className="order-next-steps"
            aria-labelledby="order-next-steps-title"
          >
            <div>
              <span className="eyebrow">Langkah selanjutnya</span>
              <h2 id="order-next-steps-title">Sekarang Anda cukup menunggu</h2>
            </div>
            <ol>
              <li>
                <span>1</span>
                <div>
                  <strong>Tunggu konfirmasi Admin</strong>
                  <p>
                    Admin {order.branchName} akan memeriksa dan mengonfirmasi
                    pesanan Anda.
                  </p>
                </div>
              </li>
              <li>
                <span>2</span>
                <div>
                  <strong>Simpan nomor pesanan</strong>
                  <p>
                    Gunakan nomor <b>{order.orderNumber}</b> jika ingin mengecek
                    pesanan kembali.
                  </p>
                </div>
              </li>
              <li>
                <span>3</span>
                <div>
                  <strong>Cek status kapan saja</strong>
                  <p>
                    Masukkan nomor pesanan dan nomor WhatsApp yang sama pada
                    halaman Cek Pesanan.
                  </p>
                </div>
              </li>
            </ol>
            <div className="order-status-guide">
              <strong>Arti perjalanan pesanan</strong>
              <p><b>Dikonfirmasi:</b> Admin sudah menerima pesanan.</p>
              <p><b>Disiapkan:</b> Barang sedang dikumpulkan.</p>
              <p><b>Dikirim / Siap diambil:</b> Barang menuju Anda atau siap di toko.</p>
              <p><b>Selesai:</b> Barang sudah diterima dan poin member sudah masuk.</p>
            </div>
            <Link className="button button--secondary" to="/pesanan">
              Cek Status Pesanan
            </Link>
          </section>
        </>
      )}
      {cancelled && (
        <Notice tone="danger">
          Pesanan ini{" "}
          {order.orderStatus === "CANCELLED" ? "dibatalkan" : "ditolak"}.
          Hubungi cabang jika Anda memerlukan bantuan.
        </Notice>
      )}
      <div className="order-status-layout">
        <section className="order-detail-card">
          {order.orderStatus !== "NEW" && (
            <div className="order-card-heading">
              <div>
                <span>Status pesanan</span>
                <StatusBadge status={order.orderStatus} />
              </div>
              <div>
                <span>Status pembayaran</span>
                <StatusBadge status={order.paymentStatus} />
              </div>
            </div>
          )}
          {!cancelled && (
            <ol
              className="order-timeline"
              aria-label="Perjalanan status pesanan"
            >
              {timelineSteps.map((step, index) => {
                const statusTime = timelineTimeByStep.get(step);
                return (
                  <li
                    className={
                      index < currentIndex
                        ? "done"
                        : index === currentIndex
                          ? "current"
                          : ""
                    }
                    data-timeline-effect={timelineEffectByStep[step]}
                    aria-current={index === currentIndex ? "step" : undefined}
                    key={step}
                  >
                    <span className="order-timeline__marker" aria-hidden="true">
                      {index < currentIndex ? (
                        <Check aria-hidden="true" />
                      ) : index === currentIndex ? (
                        <OrderTimelineCurrentIcon status={step} />
                      ) : (
                        index + 1
                      )}
                    </span>
                    <span className="order-timeline__label">
                      <strong>{statusLabel(step)}</strong>
                      {statusTime && (
                        <time dateTime={statusTime}>
                          <Clock3 aria-hidden="true" />
                          {formatDateTimeWithZone(
                            statusTime,
                            order.serviceTimeZone,
                          )}
                        </time>
                      )}
                      <span className="sr-only">
                        {index < currentIndex
                          ? "Tahap selesai"
                          : index === currentIndex
                            ? "Status pesanan saat ini"
                            : "Tahap berikutnya"}
                      </span>
                    </span>
                    {index === currentIndex && (
                      <span className="order-timeline__effect" aria-hidden="true" />
                    )}
                  </li>
                );
              })}
            </ol>
          )}
          <h2>Isi Pesanan</h2>
          <div className="order-lines">
            {order.items.map((item, index) => (
              <div key={String(item.id ?? item.productId ?? index)}>
                <span>
                  {item.quantity}× {item.productName}
                </span>
                <strong>{formatRupiah(item.subtotal)}</strong>
              </div>
            ))}
          </div>
          <div className="order-totals">
            <div>
              <span>Subtotal</span>
              <strong>{formatRupiah(order.subtotal)}</strong>
            </div>
            <div>
              <span>Ongkir</span>
              <strong>
                {order.deliveryFee ? formatRupiah(order.deliveryFee) : "Gratis"}
              </strong>
            </div>
            {order.discount > 0 && (
              <div>
                <span>Potongan {order.pointsRedeemed} poin</span>
                <strong>-{formatRupiah(order.discount)}</strong>
              </div>
            )}
            <div>
              <span>Total</span>
              <strong>{formatRupiah(order.total)}</strong>
            </div>
          </div>
          {order.pointsEarned > 0 && (
            <Notice tone="success">
              {order.pointsEarned} poin dari pesanan ini sudah masuk ke akun member.
            </Notice>
          )}
        </section>
        <aside className="order-side-card">
          <h2>Informasi Pesanan</h2>
          <dl>
            <div>
              <dt>Cabang</dt>
              <dd>{order.branchName}</dd>
            </div>
            <div>
              <dt>Nama</dt>
              <dd>{order.customerName}</dd>
            </div>
            {order.customerWhatsappMasked && (
              <div>
                <dt>WhatsApp</dt>
                <dd>{order.customerWhatsappMasked}</dd>
              </div>
            )}
            <div>
              <dt>Cara menerima</dt>
              <dd>
                {order.deliveryType === "DELIVERY"
                  ? "Antar ke alamat"
                  : "Ambil di toko"}
              </dd>
            </div>
            {order.deliveryAddress && (
              <div>
                <dt>Alamat</dt>
                <dd>{order.deliveryAddress}</dd>
              </div>
            )}
            <div>
              <dt>Pembayaran</dt>
              <dd>{order.paymentMethodName}</dd>
            </div>
          </dl>
          {whatsapp && (
            <a
              className="button button--primary button--full"
              href={whatsapp}
              target="_blank"
              rel="noreferrer"
            >
              <MessageCircle aria-hidden="true" />
              <span>Konfirmasi via WhatsApp</span>
            </a>
          )}
          <Link className="button button--secondary button--full" to="/">
            Kembali Belanja
          </Link>
        </aside>
      </div>
    </div>
  );
}

export function InformationalPage({
  type,
}: {
  type: "about" | "contact" | "privacy" | "terms";
}) {
  const { store } = useStore();
  const content = {
    about: {
      title: "Tentang Warung Sedap Sayur",
      intro:
        "Warung Sedap Sayur membantu keluarga berbelanja sayur dan kebutuhan harian dari cabang lokal dengan cara yang sederhana.",
      sections: [
        [
          "Cara kami melayani",
          "Pilih produk, buat pesanan tanpa akun, lalu lanjutkan konfirmasi melalui WhatsApp.",
        ],
        [
          "Harga yang jelas",
          "Harga, stok, pengantaran, dan pembayaran selalu mengikuti cabang yang sedang aktif.",
        ],
      ],
    },
    contact: {
      title: "Hubungi Warung Sedap Sayur",
      intro:
        "Butuh bantuan memilih produk atau menanyakan pesanan? Cabang aktif siap membantu.",
      sections: [
        [
          store?.activeBranch?.name ?? "Cabang aktif",
          `${store?.activeBranch?.address ?? "Alamat belum tersedia"}. Jam layanan ${store?.activeBranch?.openingTime ?? "07:00"}–${store?.activeBranch?.closingTime ?? "21:00"}.`,
        ],
      ],
    },
    privacy: {
      title: "Kebijakan Privasi",
      intro:
        "Kami menggunakan data seperlunya untuk membuat dan melayani pesanan Anda.",
      sections: [
        [
          "Data yang digunakan",
          "Nama, nomor WhatsApp, alamat pengantaran, dan informasi pesanan digunakan untuk pemrosesan pesanan.",
        ],
        [
          "Penyimpanan dan akses",
          "Data operasional hanya dapat diakses oleh petugas cabang terkait dan Owner sesuai kebutuhan bisnis.",
        ],
        [
          "Hubungi kami",
          "Silakan hubungi Warung Sedap Sayur jika Anda ingin menanyakan data pesanan Anda.",
        ],
      ],
    },
    terms: {
      title: "Syarat & Ketentuan",
      intro:
        "Ketentuan sederhana berikut membantu proses belanja berjalan jelas.",
      sections: [
        [
          "Ketersediaan dan harga",
          "Stok dan harga diperiksa kembali saat pesanan dibuat. Perubahan akan diinformasikan sebelum pesanan diproses.",
        ],
        [
          "Konfirmasi pesanan",
          "Pesanan tersimpan sebelum WhatsApp dibuka. Cabang dapat menghubungi Anda untuk memastikan ketersediaan dan pengantaran.",
        ],
        [
          "Pembayaran dan pembatalan",
          "Metode pembayaran mengikuti cabang. Pembatalan menyesuaikan status pemrosesan pesanan.",
        ],
      ],
    },
  }[type];
  return (
    <div className="page-shell inner-page content-page">
      <span className="eyebrow">{store?.storeName ?? "Warung Sedap Sayur"}</span>
      <h1>{content.title}</h1>
      <p className="content-page__intro">{content.intro}</p>
      {content.sections.map(([title, body]) => (
        <section key={title}>
          <h2>{title}</h2>
          <p>{body}</p>
        </section>
      ))}
    </div>
  );
}
